import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Play, Fingerprint, ScanFace } from 'lucide-react';
import { haptics } from '@/utils/haptics';
import nativeBiometricService, {
  isBiometricAvailable,
  isBiometricLoginEnabled,
  biometricLogin,
  getBiometryLabel,
  enableBiometricLogin,
  BIOMETRY_TYPE,
} from '@/services/nativeBiometricService';

/**
 * LoginPage - Modern Mobile App Login Screen
 * 
 * Features:
 * - Soft neutral background
 * - Large centered logo with animation
 * - Clean white card for form
 * - Polished iPhone app feel
 * - Try Demo button for exploring with Carter family data
 */

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState(BIOMETRY_TYPE.NONE);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [showEnableBiometric, setShowEnableBiometric] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithSession, isAuthenticated, user } = useAuth();
  const { isDemoMode, enterDemoMode } = useDemo();

  // Get the intended destination
  const from = location.state?.from?.pathname || '/';

  // Check biometric availability on mount
  useEffect(() => {
    const checkBiometric = async () => {
      const availability = await isBiometricAvailable();
      setBiometricAvailable(availability.available);
      setBiometricType(availability.biometryType);
      setBiometricEnabled(isBiometricLoginEnabled());
    };
    checkBiometric();
  }, []);

  // Handle biometric login
  const handleBiometricLogin = async () => {
    haptics.press();
    setBiometricLoading(true);

    try {
      const result = await biometricLogin();
      
      if (result.success && result.sessionToken) {
        // Use the stored session token to authenticate
        const authResult = await loginWithSession(result.sessionToken);
        
        if (authResult?.success) {
          haptics.success();
          navigate(from, { replace: true });
        } else {
          // Session expired, need to re-login with password
          toast.info('Session expired', {
            description: 'Please sign in with your password'
          });
          setBiometricEnabled(false);
        }
      } else if (result.cancelled) {
        // User cancelled, do nothing
      } else {
        toast.error(result.error || 'Biometric login failed');
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      toast.error('Biometric login failed');
    } finally {
      setBiometricLoading(false);
    }
  };

  // Handle Try Demo button
  const handleTryDemo = () => {
    haptics.press();
    enterDemoMode();
    toast.success('Welcome to Demo Mode!', {
      description: 'Explore Forget Me Not with the Carter family'
    });
    navigate('/', { replace: true });
  };

  // If already authenticated or in demo mode, redirect using Navigate component
  if (isAuthenticated || isDemoMode) {
    return <Navigate to={from} replace />;
  }

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    haptics.press();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        navigate('/2fa-verify', { 
          state: { 
            pendingToken: result.pending_token,
            totpEnabled: result.totp_enabled,
            emailEnabled: result.email_2fa_enabled
          }
        });
        return;
      }
      
      // Success - check if biometric is available and not yet enabled
      haptics.success();
      
      if (biometricAvailable && !biometricEnabled) {
        // Offer to enable biometric login
        setShowEnableBiometric(true);
        // Store credentials temporarily for enabling biometric
        window._tempLoginCreds = { 
          email, 
          sessionToken: result?.session_token,
          userId: result?.user?.user_id 
        };
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const errorCode = error.code;
      
      if (status === 401 || detail?.toLowerCase().includes('invalid') || detail?.toLowerCase().includes('password')) {
        toast.error('Email or password incorrect', {
          description: 'Please check and try again'
        });
      } else if (status === 404 || detail?.toLowerCase().includes('not found')) {
        toast.error('Account not found', {
          description: 'Would you like to create one?'
        });
      } else if (!navigator.onLine) {
        toast.error('No internet connection', {
          description: 'Please check your connection and try again'
        });
      } else if (errorCode === 'ERR_NETWORK' || errorCode === 'ERR_CONNECTION_REFUSED' || error.message?.includes('Network')) {
        haptics.error();
        toast.error('Cannot connect to server', {
          description: 'Please check your internet connection or try again later'
        });
      } else {
        haptics.error();
        // Show actual error for debugging
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        console.error('Login error:', error);
        toast.error('Something went wrong', {
          description: String(errorMsg).substring(0, 100)
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign In (Emergent OAuth)
  const handleGoogleSignIn = () => {
    haptics.press();
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Handle Apple Sign In
  const handleAppleSignIn = () => {
    haptics.press();
    toast.info('Apple Sign In', {
      description: 'Apple Sign In requires native iOS app. Coming soon to web!'
    });
  };

  // Handle enabling biometric login
  const handleEnableBiometric = async () => {
    const creds = window._tempLoginCreds;
    if (!creds) {
      navigate(from, { replace: true });
      return;
    }

    try {
      const result = await enableBiometricLogin(creds.userId, creds.email, creds.sessionToken);
      if (result.success) {
        toast.success(`${getBiometryLabel(biometricType)} enabled!`, {
          description: 'Next time, just use your face or fingerprint to sign in'
        });
        setBiometricEnabled(true);
      }
    } catch (error) {
      console.error('Failed to enable biometric:', error);
    }
    
    delete window._tempLoginCreds;
    setShowEnableBiometric(false);
    navigate(from, { replace: true });
  };

  // Skip enabling biometric
  const handleSkipBiometric = () => {
    delete window._tempLoginCreds;
    setShowEnableBiometric(false);
    navigate(from, { replace: true });
  };

  // Get biometric icon based on type
  const getBiometricIcon = () => {
    if (biometricType === BIOMETRY_TYPE.FACE_ID || biometricType === BIOMETRY_TYPE.FACE_AUTHENTICATION) {
      return <ScanFace className="h-6 w-6" />;
    }
    return <Fingerprint className="h-6 w-6" />;
  };

  // Biometric enable dialog
  if (showEnableBiometric) {
    return (
      <div className="min-h-screen bg-[#F8F7F5] flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-lg text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
            {getBiometricIcon()}
          </div>
          
          <h2 className="text-xl font-semibold text-slate-800 mb-2">
            Enable Quick Login?
          </h2>
          
          <p className="text-slate-500 mb-6">
            Use {getBiometryLabel(biometricType)} to sign in faster next time. No password needed!
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={handleEnableBiometric}
              className="w-full h-12 bg-[#5B9BD5] hover:bg-[#4A8BC5] rounded-xl"
              data-testid="enable-biometric-button"
            >
              {getBiometricIcon()}
              <span className="ml-2">Enable {getBiometryLabel(biometricType)}</span>
            </Button>
            
            <Button
              variant="ghost"
              onClick={handleSkipBiometric}
              className="w-full text-slate-500"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex flex-col">
      {/* Logo Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-16 pb-8">
        {/* Animated Logo */}
        <div className="animate-logo-enter animate-breathe mb-4">
          <svg 
            width="80" 
            height="80" 
            viewBox="0 0 100 100" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className="drop-shadow-sm"
          >
            {/* Flower petals - soft blue forget-me-not */}
            <circle cx="50" cy="30" r="14" fill="#7BB8E0" />
            <circle cx="70" cy="42" r="14" fill="#8DC4E8" />
            <circle cx="65" cy="65" r="14" fill="#7BB8E0" />
            <circle cx="35" cy="65" r="14" fill="#8DC4E8" />
            <circle cx="30" cy="42" r="14" fill="#7BB8E0" />
            {/* Center */}
            <circle cx="50" cy="50" r="10" fill="#FFE082" />
            <circle cx="50" cy="50" r="5" fill="#FFC107" />
          </svg>
        </div>
        
        {/* App Name */}
        <h1 className="text-2xl font-semibold text-slate-800 mb-1 animate-logo-enter animation-delay-100">
          Forget Me Not
        </h1>
        
        {/* Welcome Text */}
        <p className="text-slate-500 animate-logo-enter animation-delay-200">
          Welcome back
        </p>
      </div>

      {/* Form Card */}
      <div className="px-5 pb-8">
        <div 
          className="bg-white rounded-2xl p-6 space-y-5 animate-card-enter"
          style={{ 
            boxShadow: '0 2px 16px -4px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Biometric Quick Login - shown when enabled */}
          {biometricAvailable && biometricEnabled && (
            <div className="pb-4 border-b border-slate-100">
              <Button
                type="button"
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl flex items-center justify-center gap-3 shadow-lg shadow-emerald-200/50"
                data-testid="biometric-login-button"
              >
                {biometricLoading ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-base font-semibold">Verifying...</span>
                  </>
                ) : (
                  <>
                    {getBiometricIcon()}
                    <span className="text-base font-semibold">
                      Sign in with {getBiometryLabel(biometricType)}
                    </span>
                  </>
                )}
              </Button>
              
              <p className="text-center text-xs text-slate-400 mt-3">
                or sign in with email below
              </p>
            </div>
          )}

          <form onSubmit={handleEmailLogin} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 text-base px-4 rounded-xl border-slate-200 focus:border-blue-400 focus:ring-blue-400 bg-slate-50/50"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-500 hover:text-blue-600 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base px-4 pr-12 rounded-xl border-slate-200 focus:border-blue-400 focus:ring-blue-400 bg-slate-50/50"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Sign In Button */}
            <Button
              type="submit"
              className="w-full h-13 text-base font-semibold rounded-xl bg-[#5B9BD5] hover:bg-[#4A8BC5] transition-colors mt-2"
              style={{ height: '52px' }}
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-400">or continue with</span>
              </div>
            </div>

            {/* Social Login Buttons */}
            <div className="space-y-3">
              {/* Google Sign In */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 text-base rounded-xl border-slate-200 hover:bg-slate-50 transition-colors flex items-center justify-center gap-3"
                onClick={handleGoogleSignIn}
                disabled={loading}
                data-testid="google-signin-button"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              {/* Apple Sign In */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 text-base rounded-xl border-slate-200 hover:bg-slate-50 transition-colors flex items-center justify-center gap-3"
                onClick={handleAppleSignIn}
                disabled={loading}
                data-testid="apple-signin-button"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </Button>
            </div>

            {/* Try Demo Section */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-400">or explore first</span>
              </div>
            </div>

            {/* Try Demo Button */}
            <Button
              type="button"
              className="w-full h-12 text-base rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              onClick={handleTryDemo}
              disabled={loading}
              data-testid="try-demo-button"
            >
              <Play className="h-4 w-4 fill-current" />
              Try Demo
            </Button>
            <p className="text-xs text-slate-400 text-center mt-2">
              Explore with the Carter family — no signup required
            </p>
          </form>
        </div>

        {/* Sign Up Link */}
        <div className="text-center mt-6">
          <p className="text-slate-500">
            {"Don't have an account?"}{' '}
            <Link 
              to="/register" 
              className="text-blue-500 hover:text-blue-600 font-semibold"
              data-testid="signup-link"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes logoEnter {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes cardEnter {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes breathe {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.03);
          }
        }
        
        .animate-logo-enter {
          animation: logoEnter 0.5s ease-out forwards;
        }
        
        .animate-card-enter {
          animation: cardEnter 0.4s ease-out 0.2s forwards;
          opacity: 0;
        }
        
        .animate-breathe {
          animation: breathe 3s ease-in-out infinite;
          animation-delay: 0.6s;
        }
        
        .animation-delay-100 {
          animation-delay: 0.1s;
          opacity: 0;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
